import { useMemo, type JSX } from 'react';

import { Box, Chip, Stack, Typography, useTheme } from '@mui/material';

import type { JizdaListItem } from '@/api/types/Jizda';
import { formatDateCs, formatWeekdayCs, todayIsoDate } from '@/utils/date';

import { CalendarDayColumn } from './CalendarDayColumn';
import { indexByDay } from './grouping';

export interface WeekCalendarProps {
  // The 7 ISO LocalDates Mon–Sun of the displayed week.
  weekDays: string[];
  items: JizdaListItem[];
  onOpen: (id: string) => void;
}

// Minimum width of each day column so the canvas stays scrollable (Excel-like)
// rather than squashing 7 columns into a narrow viewport (zadání §6 —
// "scrollovatelné plátno").
const DAY_COL_MIN_WIDTH = 200;

// Desktop weekly canvas (CHR-03 §6 / ADR-0051): 7 day columns Po–Ne in a
// horizontally-scrollable grid; each column owns a sticky day header and its
// commodity-grouped body. Today's column is highlighted. This is the desktop
// tree only — mobile uses the DISTINCT `WeekCalendarMobile` (ADR-0029), never
// a CSS-collapsed version of this grid.
export const WeekCalendar = ({
  weekDays,
  items,
  onOpen,
}: WeekCalendarProps): JSX.Element => {
  const theme = useTheme();
  const today = todayIsoDate();

  const byDay = useMemo(() => indexByDay(items), [items]);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 3,
        boxShadow: theme.elevation.shadow1,
      }}
      data-testid="week-calendar"
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(7, minmax(${DAY_COL_MIN_WIDTH}px, 1fr))`,
          minWidth: DAY_COL_MIN_WIDTH * 7,
          minHeight: '100%',
        }}
      >
        {weekDays.map((iso, idx) => {
          const dayItems = byDay.get(iso) ?? [];
          const isToday = iso === today;
          const isLast = idx === weekDays.length - 1;
          return (
            <Box
              key={iso}
              data-testid={`calendar-day-${iso}`}
              data-today={isToday ? 'true' : 'false'}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                borderRight: isLast ? 'none' : `1px solid ${theme.palette.divider}`,
                bgcolor: isToday ? theme.palette.primary.light : 'transparent',
              }}
            >
              {/* Sticky day header — weekday abbrev + short date. The header
                  MUST stay fully OPAQUE so scrolled cells don't bleed through
                  and overlap the text: today's header therefore uses the same
                  opaque `background.default` as the other days (NOT the
                  semi-transparent `primary.light` it used before), and marks
                  "today" with an opaque 2px `primary.main` bottom accent plus
                  the `primary.dark` weekday text below. The column body keeps
                  its `primary.light` tint. */}
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  px: theme.spacing(1.5),
                  py: theme.spacing(1),
                  borderBottom: isToday
                    ? `2px solid ${theme.palette.primary.main}`
                    : `1px solid ${theme.palette.divider}`,
                  bgcolor: theme.palette.background.default,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="baseline"
                  gap={theme.spacing(0.75)}
                  // Reserve the small-Chip height (24px) on every header row so
                  // empty days (no count Chip) get the SAME header height as
                  // counted days — no ragged sticky header band (calendar-polish-3
                  // item 1). The Chip is NOT always rendered (would read as "0");
                  // only its height is reserved, with the row vertically centered.
                  sx={{ minHeight: theme.spacing(3) }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: isToday
                        ? theme.palette.primary.dark
                        : theme.palette.text.primary,
                    }}
                  >
                    {formatWeekdayCs(iso)}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: theme.monospaceFontFamily,
                      fontFeatureSettings: '"tnum"',
                      fontSize: 11,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {formatDateCs(iso)}
                  </Typography>
                  {/* Per-day fůra count (change-spec item 3) — pushed to the
                      right edge of the header by a flexible spacer, mirroring
                      the mobile day-summary Chip (`WeekCalendarMobile`): same
                      `size="small"`, same tabular-nums, shown only when the
                      column has ≥1 fůra (matches the mobile `> 0` rule). The
                      count = filtered fůry in this column (`items` is already
                      filtered upstream). */}
                  <Box sx={{ flex: 1 }} />
                  {dayItems.length > 0 ? (
                    <Chip
                      size="small"
                      label={dayItems.length}
                      sx={{ fontVariantNumeric: 'tabular-nums', alignSelf: 'center' }}
                      data-testid={`calendar-header-count-${iso}`}
                    />
                  ) : null}
                </Stack>
              </Box>
              <Box sx={{ p: theme.spacing(1), flex: 1 }}>
                <CalendarDayColumn items={dayItems} onOpen={onOpen} />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
